import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  createBillingPortalSession,
  clearBillingSessionUrl,
  unSubscribePlan,
  resetUnsubscriptionState,
} from '../../redux/subscription/subscriptionSlice';

import StyledBackButton from '../Common/StyledBackButton';
import StyledButton from '../Common/StyledButton';
import { SubscriptionPlanTypes } from '../../types/coach';
import { ClientLimitStatus } from '../../redux/coach/coachSlice';
import { getPlanRank } from '../../utils/paymentUtills';

interface ISubscriptionUpdateModalProps {
  maxClients: number | undefined;
  clientCountStatus: ClientLimitStatus | undefined;
  onClose: () => void;
  plan: SubscriptionPlanTypes | null;
  currentPlan: SubscriptionPlanTypes;
}

export default function SubscriptionUpdateModal({
  maxClients,
  clientCountStatus,
  onClose,
  plan,
  currentPlan,
}: ISubscriptionUpdateModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const {
    customerPortalLoading,
    customerPortalUrl,
    unsubscribePlanLoading,
    unsubscribePlanSuccess,
  } = useAppSelector((state) => state.subscription);
  const isUpgrade = getPlanRank(plan) > getPlanRank(currentPlan);

  const handleContinue = () => {
    if (!coachId) return;
    if (plan === SubscriptionPlanTypes.FREE) {
      dispatch(unSubscribePlan());
    } else {
      dispatch(createBillingPortalSession({ coachId }));
    }
  };

  const handleCancel = () => {
    onClose();
  };

  useEffect(() => {
    if (customerPortalUrl) {
      window.location.href = customerPortalUrl;
      dispatch(clearBillingSessionUrl());
      onClose();
    }
    if (unsubscribePlanSuccess) {
      onClose();
    }
  }, [customerPortalUrl, unsubscribePlanSuccess, unsubscribePlanLoading]);

  useEffect(
    () => () => {
      dispatch(resetUnsubscriptionState());
      dispatch(clearBillingSessionUrl());
    },
    [dispatch]
  );

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '10px',
        border: '1px solid #FFDDC8',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '167px',
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
          background: isUpgrade
            ? 'linear-gradient(0deg, #6599A51A, #3B667E1A)'
            : 'linear-gradient(#FF9A9A1A, #FF58581A)',
        }}
      >
        <img
          src={
            isUpgrade
              ? '/assets/icons/shield-warning-green.svg'
              : '/assets/icons/downgrade-warning.svg'
          }
          alt="Subscription Success"
        />
        <Typography
          variant="title2Bold"
          sx={{
            background: isUpgrade
              ? 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)'
              : 'linear-gradient(45deg, #FF9A9A, #FF5858)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
          }}
        >
          {isUpgrade ? t('payment.upgradeWarning') : t('payment.downgradeWarning')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '48px',
          padding: '52px 32px 36px 32px',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        {isUpgrade ? (
          <Typography
            variant="bodyRegular"
            sx={{
              color: '#2A3C67',
            }}
          >
            {t('payment.upgradeWarningDesc')}
          </Typography>
        ) : (
          <Typography
            variant="bodyRegular"
            sx={{
              color: '#2A3C67',
            }}
          >
            {clientCountStatus === ClientLimitStatus.LIMIT_REACHED
              ? t('payment.downgradeWarningDescForClientLimitExceed', {
                  maxClientCount: maxClients,
                })
              : t('payment.downgradeWarningDescForClientLimitNotExceed')}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '85%',
          }}
        >
          <StyledButton
            loading={
              customerPortalLoading ||
              (plan === SubscriptionPlanTypes.FREE && unsubscribePlanLoading)
            }
            onClick={handleContinue}
          >
            {t('common.continue')}
          </StyledButton>
          <StyledBackButton variant="outlined" onClick={handleCancel}>
            {t('common.cancel')}
          </StyledBackButton>
        </Box>
      </Box>
    </Box>
  );
}
